import RichText from './RichText';
import ChatLinkPreview from './ChatLinkPreview';
import MentionTextarea from './MentionTextarea';
import SharePost from './SharePost';
import EmojiReactions from './EmojiReactions';
import SolvedPicker from './SolvedPicker';
import ReferralPanel from './Referral';
import { openProfile } from '../lib/nav';
import Avatar from './Avatar';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { isAdminNow } from '../lib/role';
import { api, getMe } from '../lib/api';
import Icon from './Icon';
import { ReplyQuote, ReplyingTo } from './ReplyQuote';
import { TYPE_BY_KEY, isRequest } from '../lib/requests';

const DISMISS_OPTIONS = [
  ['topic', "Not this topic"],
  ['person', 'Not this person'],
  ['timing', 'Not right now'],
];

function timeAgo(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.max(1, Math.round((Date.now() - then) / 60000));
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}


/* The page's own title, blurb and image, fetched when the post was written. */
function LinkPreview({ card }) {
  if (!card?.url) return null;
  return (
    <a
      className="fc-preview"
      href={card.url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      onClick={(e) => e.stopPropagation()}
    >
      {card.image && <img src={card.image} alt="" loading="lazy" referrerPolicy="no-referrer" />}
      <span className="fc-preview-text">
        <span className="fc-preview-site">{card.site}</span>
        <b>{card.title}</b>
        {card.description && <span className="fc-preview-desc">{card.description}</span>}
      </span>
    </a>
  );
}

/* Your own comments can be fixed (typos) or removed (a duplicate). */
function Comment({ c, mine, myId, postId, notify, onChanged, onDeleted, onReply }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(c.body);
  const [busy, setBusy] = useState(false);
  async function save() {
    if (!draft.trim() || busy) return;
    setBusy(true);
    try {
      onChanged?.(await api.editComment(c.id, draft.trim()));
      setEditing(false);
    } catch (e) { notify?.(e.message); } finally { setBusy(false); }
  }
  async function remove() {
    if (!window.confirm('Delete this comment?')) return;
    try { await api.deleteComment(c.id); onDeleted?.(c.id); notify?.('Comment deleted'); } catch (e) { notify?.(e.message); }
  }
  return (
    <li id={`msg-${c.id}`} className={`fc-c${mine ? ' mine' : ''}`}>
      <button type="button" className="person-link" aria-label={`${c.author?.name}'s profile`} onClick={() => openProfile(c.author?.handle)}>
        <Avatar person={c.author} className="fc-c-av" />
      </button>
      <div className="fc-c-bubble">
        <div className="fc-c-head">
          <button type="button" className="person-link name" onClick={() => openProfile(c.author?.handle)}><b>{c.author?.name}</b></button>
          {mine && <span className="fc-c-you">you</span>}
          <span className="muted-sm">{timeAgo(c.created_at)}{c.edited ? ' · edited' : ''}</span>
          {!mine && onReply && (
            <span className="fc-c-tools">
              <button type="button" className="linklike" onClick={() => onReply(c)}>Reply</button>
            </span>
          )}
          {mine && !editing && (
            <span className="fc-c-tools">
              <button type="button" className="linklike" onClick={() => { setDraft(c.body); setEditing(true); }}>Edit</button>
              <button type="button" className="linklike" onClick={remove}>Delete</button>
            </span>
          )}
        </div>
        {editing ? (
          <div className="fc-c-edit">
            <MentionTextarea className="ta" value={draft} postId={postId} autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save(); }} />
            <div className="fc-c-edit-actions">
              <button type="button" className="btn quiet sm" onClick={() => setEditing(false)}>Cancel</button>
              <button type="button" className="btn primary sm" disabled={!draft.trim() || busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        ) : (
          <>
            <ReplyQuote q={c.reply_to} />
            <p><RichText text={c.body} mentions={c.mentions} /></p>
            <ChatLinkPreview text={c.body} />
          </>
        )}
        {!editing && (
          <EmojiReactions reactions={c.reactions} myId={myId} label="this comment"
            onReact={(e) => api.reactToComment(c.id, e).then(onChanged).catch((err) => notify?.(err.message))} />
        )}
      </div>
    </li>
  );
}

/* In the feed a post is a preview: a line or three, then "Read more" —
   so people see a few more posts per screen. The full text is a click away. */
function Clamp({ lines, children }) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  const [all, setAll] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && !all) setOver(el.scrollHeight > el.clientHeight + 1);
  }, [children, all]);
  return (
    <>
      <div ref={ref} className={all ? 'fc-text' : 'fc-text fc-clamp'} style={all ? undefined : { WebkitLineClamp: lines }}>{children}</div>
      {(over || all) && (
        <button type="button" className="linklike fc-readmore" onClick={(e) => { e.stopPropagation(); setAll(!all); }}>
          {all ? 'Show less' : 'Read more'}
        </button>
      )}
    </>
  );
}

/* `open` shows the whole thread and the comment box straight away — the post
   page. In the feed a card shows the latest two and expands in place. */
export default function FeedCard({ item, notify, onOpen, open = false, onRequestChanged, onSavedChanged }) {
  const isNews = item.type === 'news';
  const myId = getMe()?.id;
  const [reacted, setReacted] = useState(item.reacted);
  // Posts: shared emoji (everyone sees who); news keeps the heart.
  const [postReactions, setPostReactions] = useState(item.reactions || []);
  // What's shown, so an edit appears at once without a reload.
  const [shown_, setShown] = useState({ title: item.title, body: item.body, mentions: item.mentions, edited: item.edited,
    preview: item.fields?.preview });
  const [editingPost, setEditingPost] = useState(false);
  const [draftTitle, setDraftTitle] = useState(item.title || '');
  const [draftBody, setDraftBody] = useState(item.body || '');
  const [savingPost, setSavingPost] = useState(false);
  const [showDismiss, setShowDismiss] = useState(false);
  const [comment, setComment] = useState('');
  const [replyFocus, setReplyFocus] = useState(0);
  // The comment you're answering: quoted above your reply.
  const [replyTo, setReplyTo] = useState(null);
  const [showComment, setShowComment] = useState(open);
  const [comments, setComments] = useState(item.recent_comments || []);
  const [count, setCount] = useState(item.comment_count || 0);
  const [loadingAll, setLoadingAll] = useState(false);
  const [posting, setPosting] = useState(false);
  const [gone, setGone] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [saved, setSaved] = useState(Boolean(item.saved));
  const [moreOpen, setMoreOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [solving, setSolving] = useState(false);
  // Solved / reopened here shows at once, wherever the card is (the post page too).
  const [state, setState] = useState(item.state);
  // Waiting for (or refused) an admin's approval: not out in the network yet.
  const live = isNews || state === 'open' || state === 'filled' || state === 'expired';

  if (gone) return null;
  // The feed shows the latest comment; the post page (and "View all") shows them all.
  const shown = open || expanded ? comments : comments.slice(-1);

  async function toggleReact() {
    try {
      const r = await api.react(item.id, isNews);
      setReacted(r.reacted);
    } catch (e) {
      notify(e.message);
    }
  }

  async function dismiss(dimension) {
    try {
      await api.dismiss(item.id, isNews, dimension);
      setGone(true);
      notify(
        dimension === 'topic'
          ? 'Noted — less of this topic'
          : dimension === 'person'
            ? 'Noted — less from this person'
            : 'Noted — not right now'
      );
    } catch (e) {
      notify(e.message);
    }
  }

  async function showAll() {
    setExpanded(true);
    setLoadingAll(true);
    try {
      const all = await api.comments(item.id, isNews);
      setComments(all);
      setCount(all.length);
    } catch (e) {
      notify(e.message);
    } finally {
      setLoadingAll(false);
    }
  }

  // Your comment goes straight into the thread — no reload, no toast. Seeing
  // it there is the confirmation.
  async function submitComment() {
    if (!comment.trim() || posting) return;
    setPosting(true);
    try {
      const saved = await api.comment(item.id, comment.trim(), isNews, replyTo?.id);
      setComments((list) => [...list, saved]);
      setCount((n) => n + 1);
      setComment('');
      setReplyTo(null);
      if (!open) setShowComment(false);
    } catch (e) {
      notify(e.message);
    } finally {
      setPosting(false);
    }
  }

  const openable = !isNews && onOpen;
  const mine = !isNews && item.author?.id === myId;

  async function toggleResolved() {
    try {
      if (state === 'filled') {
        await api.reopenRequest(item.id);
        setState('open');
        notify('Reopened — back on your Open Requests');
      } else {
        await api.resolveRequest(item.id);
        setState('filled');
        notify('Marked solved — nice');
      }
      onRequestChanged?.();
    } catch (e) {
      notify(e.message);
    }
  }

  return (
    <article className={`card feedcard${open ? '' : ' in-feed'}${item.is_exploration ? ' exploring' : ''}${isRequest(item.kind) ? ` k-${item.kind}` : ''}`}>
      <header className="fc-head">
        {isNews ? (
          <>
            <span className="fc-src">{item.source}</span>
            <span className="fc-dot">·</span>
            <span className="muted-sm">{timeAgo(item.created_at)}{shown_.edited ? ' · edited' : ''}</span>
          </>
        ) : (
          <>
            <button type="button" className="person-link" aria-label={`${item.author?.name}'s profile`} onClick={() => openProfile(item.author?.handle)}>
              <Avatar person={item.author} className="fc-av" />
            </button>
            <span className="fc-who">
              <button type="button" className="person-link name" onClick={() => openProfile(item.author?.handle)}><b>{item.author?.name}</b></button>
              {item.author?.headline && <span className="fc-headline">{item.author.headline}</span>}
            </span>
            <span className="muted-sm">{timeAgo(item.created_at)}{shown_.edited ? ' · edited' : ''}</span>
          </>
        )}
        {isRequest(item.kind) && (
          <span className={`req-badge k-${item.kind}`}>
            <Icon name={TYPE_BY_KEY[item.kind].icon} size={13} /> {TYPE_BY_KEY[item.kind].label.replace('…', '')}
          </span>
        )}
        {isRequest(item.kind) && state === 'filled' && (
          <span className="req-resolved"><Icon name="done" size={13} /> Resolved</span>
        )}
      </header>

      <div
        className={`fc-body${openable ? ' openable' : ''}`}
        onClick={openable ? () => onOpen(item.id) : undefined}
        onKeyDown={openable ? (e) => { if (e.key === 'Enter') onOpen(item.id); } : undefined}
        role={openable ? 'link' : undefined}
        tabIndex={openable ? 0 : undefined}
      >
        {editingPost ? (
          <div className="fc-post-edit" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
            {(shown_.title || isRequest(item.kind)) && (
              <input className="in" value={draftTitle} maxLength={90} aria-label="Headline" onChange={(e) => setDraftTitle(e.target.value)} />
            )}
            <MentionTextarea className="ta" value={draftBody} postId={item.id} onChange={(e) => setDraftBody(e.target.value)} />
            <div className="fc-c-edit-actions">
              <button type="button" className="btn quiet sm" onClick={() => setEditingPost(false)}>Cancel</button>
              <button type="button" className="btn primary sm" disabled={savingPost || (!draftBody.trim() && !draftTitle.trim())}
                onClick={async () => {
                  setSavingPost(true);
                  try {
                    const out = await api.editPost(item.id, { title: draftTitle, body: draftBody });
                    setShown({ title: out.title, body: out.body, mentions: out.mentions, edited: true, preview: out.fields?.preview });
                    setEditingPost(false);
                    notify?.('Post updated');
                  } catch (e) { notify?.(e.message); } finally { setSavingPost(false); }
                }}>{savingPost ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        ) : (<>
          {shown_.title && (
            <h3>
              {isNews && item.url ? (
                <a href={item.url} target="_blank" rel="noreferrer">{shown_.title}</a>
              ) : (
                shown_.title
              )}
            </h3>
          )}
          {shown_.body && (open ? <p>{isNews ? shown_.body : <RichText text={shown_.body} mentions={shown_.mentions} />}</p> : (
            <Clamp lines={shown_.title ? 1 : 3}>{isNews ? shown_.body : <RichText text={shown_.body} mentions={shown_.mentions} />}</Clamp>
          ))}
        </>)}
        {!isNews && item.kind === 'refer' && <ReferralPanel item={state === item.state ? item : { ...item, state }} mine={mine} notify={notify} onChanged={onRequestChanged} />}
        {!isNews && <LinkPreview card={shown_.preview} />}
        {item.categories?.length > 0 && (
          <div className="fc-tags">
            {item.categories.map((c) => <span className="tag" key={c}>{c}</span>)}
          </div>
        )}
      </div>

      {/* Why it's here only explains a ranking — News is ranked, Home is newest first. */}
      {isNews && item.reason && <div className="fc-why">{item.reason}</div>}

      {/* Author-only, and by name rather than by number. At this size the point
          is "somebody was here", which a hidden count cannot say and a public
          one would turn into a scoreboard. */}
      {isNews && item.reactors?.length > 0 && (
        <p className="fc-reactors">
          {item.reactors.length === 1
            ? `${item.reactors[0].name} reacted to this`
            : item.reactors.length === 2
              ? `${item.reactors[0].name} and ${item.reactors[1].name} reacted to this`
              : `${item.reactors.slice(0, 2).map((r) => r.name).join(', ')} and ${item.reactors.length - 2} other${item.reactors.length - 2 > 1 ? 's' : ''} reacted to this`}
        </p>
      )}

      <footer className="fc-actions">
        {isNews ? (
          <button className={reacted ? 'on' : ''} onClick={toggleReact} aria-pressed={reacted}>
            <Icon name="heart" size={18} className={reacted ? 'filled' : ''} />
            {reacted ? 'Reacted' : 'React'}
          </button>
        ) : (
          <EmojiReactions reactions={postReactions} myId={myId} label="this post"
            onReact={(e) => api.reactToPost(item.id, e).then((r) => setPostReactions(r.reactions)).catch((err) => notify?.(err.message))} />
        )}
        <button onClick={() => setShowComment(!showComment)} aria-expanded={showComment}>
          <Icon name="comment" size={18} />
          Comment{count ? ` (${count})` : ''}
        </button>
        {!isNews && live && (
          <button onClick={() => setSharing(!sharing)} aria-expanded={sharing}>
            <Icon name="send" size={18} />
            Share
          </button>
        )}
        {isNews && (
          <button onClick={() => setShowDismiss(!showDismiss)} aria-expanded={showDismiss}>
            <Icon name="hide" size={18} />
            Not for me
          </button>
        )}
        {!isNews && (
          <div className="fc-more">
            <button className="fc-more-btn" aria-label={mine ? 'More: Save, Edit, Delete, Mark solved' : 'More: Save, Not for me'} aria-haspopup="menu"
              aria-expanded={moreOpen} onClick={() => setMoreOpen(!moreOpen)}>⋯</button>
            {moreOpen && (
              <div className="fc-more-menu" role="menu" onMouseLeave={() => setMoreOpen(false)}>
                <button role="menuitem" onClick={async () => {
                  setMoreOpen(false);
                  try {
                    if (saved) await api.unsavePost(item.id); else await api.savePost(item.id);
                    setSaved(!saved);
                    notify?.(saved ? 'Removed from Saved' : 'Saved — find it under My activity → Saved');
                    onSavedChanged?.();
                  } catch (e) { notify?.(e.message); }
                }}>
                  <Icon name="bookmark" size={16} className={saved ? 'filled' : ''} /> {saved ? 'Remove from Saved' : 'Save'}
                </button>
                {mine && (
                  <button role="menuitem" onClick={() => {
                    setMoreOpen(false); setDraftTitle(shown_.title || ''); setDraftBody(shown_.body || ''); setEditingPost(true);
                  }}>
                    <Icon name="compose" size={16} /> Edit post
                  </button>
                )}
                {mine && (
                  <button role="menuitem" onClick={async () => {
                    setMoreOpen(false);
                    if (!window.confirm('Delete this post? Its comments and reactions go with it.')) return;
                    try { await api.deletePost(item.id); setGone(true); notify?.('Post deleted'); onRequestChanged?.(); }
                    catch (e) { notify?.(e.message); }
                  }}>
                    <Icon name="trash" size={16} /> Delete post
                  </button>
                )}
                {!mine && !isNews && isAdminNow() && (
                  <button role="menuitem" className="danger" onClick={async () => {
                    setMoreOpen(false);
                    if (!window.confirm(`Remove this post by ${item.author?.name || 'this member'} for everyone? Its comments and reactions go with it. This can't be undone.`)) return;
                    try { await api.deletePost(item.id); setGone(true); notify?.('Post removed'); onRequestChanged?.(); }
                    catch (e) { notify?.(e.message); }
                  }}>
                    <Icon name="trash" size={16} /> Remove post (admin)
                  </button>
                )}
                {mine && isRequest(item.kind) && live && (
                  <button role="menuitem" onClick={() => {
                    setMoreOpen(false);
                    if (state === 'filled') toggleResolved(); else setSolving(true);
                  }}>
                    <Icon name="done" size={16} /> {state === 'filled' ? 'Reopen request' : 'Mark solved'}
                  </button>
                )}
                {!mine && (
                  <button role="menuitem" onClick={() => { setMoreOpen(false); setShowDismiss(true); }}>
                    <Icon name="hide" size={16} /> Not for me
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {isNews && item.url && (
          <a className="fc-open" href={item.url} target="_blank" rel="noreferrer">
            Read <Icon name="external" size={16} />
          </a>
        )}
      </footer>

      {solving && (
        <SolvedPicker postId={item.id} notify={notify} onCancel={() => setSolving(false)}
          onDone={() => { setSolving(false); setState('filled'); onRequestChanged?.(); }} />
      )}
      {sharing && <SharePost postId={item.id} notify={notify} onClose={() => setSharing(false)} />}

      {showDismiss && (
        <div className="fc-dismiss">
          <span className="muted-sm">Why? This teaches the matcher — an unlabelled no teaches it nothing.</span>
          <div>
            {DISMISS_OPTIONS.map(([value, label]) => (
              <button key={value} className="btn quiet sm" onClick={() => dismiss(value)}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {comments.length > 0 && (
        <div className="fc-comments">
          {count > shown.length && (
            <button className="linklike" onClick={showAll} disabled={loadingAll}>
              {loadingAll ? 'Loading…' : `View all ${count} comments`}
            </button>
          )}
          <ul>
            {shown.map((c) => (
              <Comment key={c.id} c={c} mine={c.author?.id === myId} myId={myId} postId={isNews ? undefined : item.id} notify={notify}
                onChanged={(u) => setComments((cs) => cs.map((x) => (x.id === u.id ? u : x)))}
                onDeleted={(id) => { setComments((cs) => cs.filter((x) => x.id !== id)); setCount((n) => Math.max(0, n - 1)); }}
                // News comments don't tag anyone, so there's no Reply there.
                onReply={c.author?.handle && !isNews ? (rc) => {
                  // Reply = a comment that tags them, so they're notified and it reads as an answer.
                  const tagText = `@${rc.author.handle} `;
                  const already = new RegExp(`@${rc.author.handle}(?![a-z0-9-])`, 'i');
                  setComment((cur) => (already.test(cur) ? cur : tagText + cur));
                  setReplyTo(rc);
                  setShowComment(true);
                  setReplyFocus(Date.now());
                } : undefined} />
            ))}
          </ul>
        </div>
      )}

      {showComment && <ReplyingTo to={replyTo} onCancel={() => setReplyTo(null)} />}
      {showComment && (
        <div className="fc-comment">
          <MentionTextarea
            className="ta" value={comment} placeholder="Say something useful… (type @ to tag someone)"
            onChange={(e) => setComment(e.target.value)}
            autoFocus={!open}
            focusKey={replyFocus}
            postId={isNews ? undefined : item.id}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submitComment(); } }}
          />
          <button className="btn primary sm" onClick={submitComment} disabled={!comment.trim() || posting}>
            {posting ? 'Posting…' : 'Comment'}
          </button>
        </div>
      )}
    </article>
  );
}
